import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select, { components as RSComponents } from 'react-select';
import { useSelector } from 'react-redux';
import { groupSalesUnitsByRegion } from './helpers';

// ── Custom Option with checkbox ───────────────────────────────────────────────
const CheckboxOption = (props) => {
  const { isSelected, label } = props;
  return (
    <RSComponents.Option {...props}>
      <div className="tw-flex tw-items-center tw-gap-2">
        <input
          type="checkbox"
          readOnly
          checked={isSelected}
          className="tw-w-3.5 tw-h-3.5 tw-accent-primary-600 tw-cursor-pointer tw-shrink-0"
        />
        <span className="tw-text-xs tw-text-slate-700">{label}</span>
      </div>
    </RSComponents.Option>
  );
};
CheckboxOption.propTypes = { isSelected: PropTypes.bool, label: PropTypes.string };

// ── Group heading with a select-all checkbox ──────────────────────────────────
// Ticking the region header selects every unit in that region; unticking clears
// them. Shows an indeterminate state when only some units in the region are
// selected. Wiring (current values + toggle handler) is passed through the
// Select's `selectProps` so the header stays in sync with the control.
const CheckboxGroupHeading = (props) => {
  const { data, selectProps } = props;
  const groupOptions   = data.options || [];
  const groupValues    = groupOptions.map((o) => o.value);
  const selectedValues = selectProps.selectedValues || [];

  const selectedCount = groupValues.filter((v) => selectedValues.includes(v)).length;
  const allSelected   = groupValues.length > 0 && selectedCount === groupValues.length;
  const someSelected  = selectedCount > 0 && !allSelected;

  const checkboxRef = useRef(null);
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleGroup = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectProps.onToggleGroup?.(groupOptions);
  };

  return (
    <RSComponents.GroupHeading {...props}>
      <div
        className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
        // Keep the menu open + control focused when the header is clicked.
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={toggleGroup}
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          readOnly
          checked={allSelected}
          className="tw-w-3.5 tw-h-3.5 tw-accent-primary-600 tw-cursor-pointer tw-shrink-0"
        />
        <span>{props.children}</span>
      </div>
    </RSComponents.GroupHeading>
  );
};
CheckboxGroupHeading.propTypes = {
  data:        PropTypes.object,
  selectProps: PropTypes.object,
  children:    PropTypes.node,
};

// ── ValueContainer: compact — shows "{n} units selected" when > 1 ─────────────
const ValueContainer = ({ children, getValue, ...props }) => {
  const selected = getValue();
  if (selected.length > 1) {
    const input = Array.isArray(children) ? children[children.length - 1] : null;
    return (
      <RSComponents.ValueContainer {...props} getValue={getValue}>
        <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 600, whiteSpace: 'nowrap', paddingLeft: 6 }}>
          {selected.length} units selected
        </span>
        {input}
      </RSComponents.ValueContainer>
    );
  }
  return <RSComponents.ValueContainer {...props} getValue={getValue}>{children}</RSComponents.ValueContainer>;
};
ValueContainer.propTypes = { children: PropTypes.node, getValue: PropTypes.func };

const COMPONENTS = { Option: CheckboxOption, ValueContainer, GroupHeading: CheckboxGroupHeading };

const selectStyles = {
  control: (b, s) => ({
    ...b,
    minHeight: 34,
    fontSize: 12,
    borderColor: s.isFocused ? '#6366f1' : '#d1d5db',
    boxShadow: s.isFocused ? '0 0 0 2px #ede9fe' : 'none',
    '&:hover': { borderColor: '#6366f1' },
  }),
  valueContainer: (b) => ({ ...b, flexWrap: 'nowrap', overflow: 'hidden', maxWidth: 160 }),
  multiValue:     (b) => ({ ...b, minWidth: 0, flexShrink: 1, maxWidth: 90, backgroundColor: '#ede9fe' }),
  multiValueLabel: (b) => ({ ...b, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 70, color: '#4f46e5', fontSize: 11 }),
  multiValueRemove: (b) => ({ ...b, color: '#6366f1', ':hover': { backgroundColor: '#c7d2fe', color: '#3730a3' } }),
  menu:            (b) => ({ ...b, zIndex: 1050, fontSize: 12 }),
  menuPortal:      (b) => ({ ...b, zIndex: 1050 }),
  groupHeading:    (b) => ({ ...b, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280', backgroundColor: '#f9fafb', padding: '6px 12px' }),
  option:          (b, s) => ({ ...b, backgroundColor: s.isFocused ? '#ede9fe' : '#fff', padding: '6px 12px' }),
};

// ── SalesUnitSelect ───────────────────────────────────────────────────────────
const SalesUnitSelect = ({ value, onChange, placeholder, width, menuPortalTarget, minHeight = 34 }) => {
  const activeSalesUnits = useSelector((s) => s.crm_sales_unit.activeSalesUnitsData);

  const options     = useMemo(() => groupSalesUnitsByRegion(activeSalesUnits), [activeSalesUnits]);
  const flatOptions = useMemo(() => (options || []).flatMap((g) => g.options || []), [options]);

  const selected = flatOptions.filter((o) => value.includes(o.value));

  // Select/deselect every unit in a region when its header checkbox is toggled.
  const handleToggleGroup = (groupOptions) => {
    const groupValues = groupOptions.map((o) => o.value);
    const allSelected = groupValues.length > 0 && groupValues.every((v) => value.includes(v));
    onChange(
      allSelected
        ? value.filter((v) => !groupValues.includes(v))
        : [...new Set([...value, ...groupValues])]
    );
  };

  // react-select's own indicator + value-container padding keeps the control
  // ~36–38px tall regardless of minHeight. At 34 and below (the dense filter
  // rows: this control sits beside 34px-tall py-1.5/text-sm inputs), compress
  // those paddings so the control actually hits `minHeight` and lines up.
  const compact = minHeight <= 34;
  const styles = {
    ...selectStyles,
    control: (b, s) => ({ ...selectStyles.control(b, s), minHeight, width: width || 'auto' }),
    ...(compact
      ? {
          valueContainer: (b) => ({ ...selectStyles.valueContainer(b), padding: '0 6px' }),
          dropdownIndicator: (b) => ({ ...b, padding: 4 }),
          clearIndicator: (b) => ({ ...b, padding: 4 }),
        }
      : {}),
  };

  return (
    <Select
      isMulti
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      value={selected}
      onChange={(sel) => onChange((sel || []).map((o) => o.value))}
      selectedValues={value}
      onToggleGroup={handleToggleGroup}
      placeholder={placeholder}
      classNamePrefix="su"
      menuPortalTarget={menuPortalTarget ?? document.body}
      styles={styles}
      components={COMPONENTS}
      filterOption={(opt, input) => {
        if (!input) return true;
        const q = input.toLowerCase();
        return opt.label.toLowerCase().includes(q) || opt.data?.__groupLabel?.toLowerCase().includes(q);
      }}
    />
  );
};

SalesUnitSelect.propTypes = {
  value:            PropTypes.array.isRequired,
  onChange:         PropTypes.func.isRequired,
  placeholder:      PropTypes.string,
  width:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  menuPortalTarget: PropTypes.object,
  minHeight:        PropTypes.number,
};

SalesUnitSelect.defaultProps = {
  placeholder: 'All units…',
};

export default SalesUnitSelect;
